import { useEffect, useReducer, useRef } from "react";
import { WarningFilled } from "@ant-design/icons";
import ScrollX from "components/ScrollX";
import { Grid, Alert, Box, InputBase } from "@mui/material";
import { useSelector } from "react-redux";
import { PAGINATION } from "utils/constants/pagination";
import MainCard from "components/MainCard";
import Timesheet from "./timesheet";
import { dispatch } from "store";
import { fetchTimesheet } from "store/reducers/timesheet";
import { get } from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import { styled, alpha } from "@mui/material/styles";
import T from "utils/constants/T";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  border: "1px solid #d9d9d9",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const TimesheetPage = () => {
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const { companyTabData, timesheet: timesheetStore } = useSelector(
    (state: any) => state
  );
  const companyId = get(selectedCompany, "_id", "");
  const { systemPayrollCode } = companyTabData;
  const {
    timesheet,
    totalRecords,
    totalPages,
  } = timesheetStore;
  const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;
  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      pageNumber: INITIAL_PAGE,
      rowsPerPage: ROWS_PER_PAGE,
      employeeName: "",
      debouncedSearchTerm: "",
    }
  );

  const { pageNumber, rowsPerPage, employeeName, debouncedSearchTerm } = localState;
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    dispatch(fetchCompanyDetails(companyId));
  }, [companyId])

  const fetchData = async () => {
    await dispatch(
      fetchTimesheet(pageNumber, rowsPerPage, companyId, debouncedSearchTerm)
    );
  };

  const handlePageChange = async (page: number) => {
    setLocalState({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);
    setLocalState({ pageNumber: INITIAL_PAGE, rowsPerPage: value });
  };

  const handleEmployeeNameSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setLocalState({ employeeName: value });
    
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to update search term after user stops typing
    debounceTimerRef.current = setTimeout(() => {
      setLocalState({ debouncedSearchTerm: value, pageNumber: INITIAL_PAGE });
    }, 800);
  };

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [pageNumber, rowsPerPage, debouncedSearchTerm, companyId]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {!companyId && (
            <Alert color="warning" icon={<WarningFilled />}>
              Please select a company first
            </Alert>
          )}
          {companyId && (
            <MainCard>
              <Box sx={{ display: "flex", gap: 1, marginLeft: "20px" }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder={T.EMPLOYEE}
                    name="employeeName"
                    value={employeeName}
                    inputProps={{ "aria-label": "search" }}
                    onChange={handleEmployeeNameSearch}
                  />
                </Search>
              </Box>
              <ScrollX>
                <Timesheet
                  timesheet={timesheet}
                  page={pageNumber}
                  pageSize={rowsPerPage}
                  totalRecords={totalRecords}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  systemPayrollCode={systemPayrollCode}
                />
              </ScrollX>
            </MainCard>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default TimesheetPage;
